// src/components/multiplayer/CreateGamePanel.tsx
import React, { useState } from "react";
import "./CreateGamePanel.css";

interface CreateGamePanelProps {
  onCreateGame: (config: GameConfiguration) => void;
  isCreating?: boolean;
  userVariants?: Array<{ id: string; name: string }>;
}

export interface GameConfiguration {
  gameName: string;
  presetId: string;
  variantId?: string;
}

export const CreateGamePanel: React.FC<CreateGamePanelProps> = ({
  onCreateGame,
  isCreating = false,
  userVariants = [],
}) => {
  // Mode selection: "preset" or "custom"
  const [mode, setMode] = useState<"preset" | "custom">("preset");

  // Form state
  const [presetId, setPresetId] = useState<string>("0808-001");
  const [variantId, setVariantId] = useState<string>("");

  // Game preset options (board size presets)
  const gamePresets = [
    { id: "0808-001", name: "Traditional FIDE Chess (8x8)", boardSize: 8 },
    { id: "0909-001", name: "Perfect Chess (9x9)", boardSize: 9 },
    { id: "1010-001", name: "Breakout Chess (10x10)", boardSize: 10 },
    { id: "1111-001", name: "Next Level Chess (11x11)", boardSize: 11 },
    { id: "1212-001", name: "Arrival Chess (12x12)", boardSize: 12 },
  ];

  // Handle form submission
  const handleCreateGame = () => {
    const config: GameConfiguration = {
      gameName: `Game ${Date.now()}`,
      presetId: mode === "preset" ? presetId : "custom",
      variantId: mode === "custom" ? variantId : undefined,
    };

    onCreateGame(config);
  };

  // Get selected preset details
  const selectedPreset = gamePresets.find(p => p.id === presetId);

  // Get selected variant details
  const selectedVariant = userVariants.find(v => v.id === variantId);

  return (
    <div className="create-game-panel-container">
      {/* Blue Notice Box */}
      <div className="create-game-notice">
        <span className="notice-icon">ℹ️</span>
        <span className="notice-text">Configure your game settings and create a new lobby</span>
      </div>

      {/* Compact 2-Column Form */}
      <div className="create-game-form">
        
        {/* Row 1: Mode Selector and Board Preset */}
        <div className="form-row">
          {/* Mode Selector */}
          <div className="form-field">
            <label className="form-label">Mode</label>
            <div className="mode-selector-compact">
              <button
                type="button"
                className={`mode-btn ${mode === "preset" ? "active" : ""}`}
                onClick={() => setMode("preset")}
                disabled={isCreating}
              >
                Standard
              </button>
              <button
                type="button"
                className={`mode-btn ${mode === "custom" ? "active" : ""}`}
                onClick={() => setMode("custom")}
                disabled={isCreating}
              >
                Custom
              </button>
            </div>
          </div>

          {/* Board Size Preset */}
          {mode === "preset" && (
            <div className="form-field">
              <label htmlFor="board-preset" className="form-label">
                Board Size
              </label>
              <select
                id="board-preset"
                className="form-select"
                value={presetId}
                onChange={(e) => setPresetId(e.target.value)}
                disabled={isCreating}
              >
                {gamePresets.map(preset => (
                  <option key={preset.id} value={preset.id}>
                    {preset.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Variant Selector (Custom Mode) */}
          {mode === "custom" && (
            <div className="form-field">
              <label htmlFor="variant" className="form-label">
                Variant
              </label>
              <select
                id="variant"
                className="form-select"
                value={variantId}
                onChange={(e) => setVariantId(e.target.value)}
                disabled={isCreating}
              >
                <option value="">Select a variant...</option>
                {userVariants.map(variant => (
                  <option key={variant.id} value={variant.id}>
                    {variant.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Configuration Summary - Compact */}
        <div className="config-summary-compact">
          <div className="summary-item">
            <span className="summary-label">Mode:</span>
            <span className="summary-value">
              {mode === "preset" ? "Standard" : "Custom"}
            </span>
          </div>
          {mode === "preset" && selectedPreset && (
            <div className="summary-item">
              <span className="summary-label">Board:</span>
              <span className="summary-value">{selectedPreset.boardSize}×{selectedPreset.boardSize}</span>
            </div>
          )}
          {mode === "custom" && (
            <div className="summary-item">
              <span className="summary-label">Variant:</span>
              <span className="summary-value">
                {selectedVariant?.name || <em>Not selected</em>}
              </span>
            </div>
          )}
        </div>

        {/* Create Button - Centered, Not Full Width */}
        <div className="form-actions">
          <button
            className={`create-game-btn ${isCreating ? "creating" : ""}`}
            onClick={handleCreateGame}
            disabled={isCreating || (mode === "custom" && !variantId)}
          >
            {isCreating ? (
              <>
                <span className="btn-spinner"></span>
                <span className="btn-text">Creating...</span>
              </>
            ) : (
              <>
                <span className="btn-icon">➕</span>
                <span className="btn-text">Create Game</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateGamePanel;
